<script lang="ts" setup>
import type { SancareCheckboxGroupItem } from '@sancare/typescript-commons'
import { SancareCheckboxGroup, SancareModal, SancareOcticon } from '@sancare/typescript-commons'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import stayListColumnGroups from '@/stay-list/stay-list-table/StayListColumnGroups'
import { StayListColumns } from '@/store/modules/settings/types'

const props = defineProps<{
  mode: string
  userColumns: string[]
  selectableColumns: StayListColumns
}>()

const emit = defineEmits<{ 'set-columns': [columns: string[]] }>()

const { t } = useI18n()
const showModal = ref(false)
const enabledColumns = ref<string[]>([])

const visibleGroups = computed(() => {
  const groupMap = new Map<string, { key: string, items: SancareCheckboxGroupItem[] }>()
  for (const [key, col] of Object.entries(props.selectableColumns)) {
    if (!groupMap.has(col.columnGroup)) {
      groupMap.set(col.columnGroup, {
        key: col.columnGroup,
        items: [],
      })
    }
    groupMap.get(col.columnGroup)!.items.push({
      key,
      label: (col.isTranslated && col.label) ? t(col.label) : (col.label ?? key),
    })
  }
  for (const group of groupMap.values()) {
    group.items.sort((a, b) => a.label.localeCompare(b.label))
  }
  return stayListColumnGroups
    .filter((groupKey) => groupMap.has(groupKey))
    .map((groupKey) => groupMap.get(groupKey)!)
})

const leftColumnGroups = ['administrativeInfo', 'sancareState', 'sancareCodingAssistance']
const leftGroups = computed(() => visibleGroups.value.filter((group) => leftColumnGroups.includes(group.key)))
const rightGroups = computed(() => visibleGroups.value.filter((group) => !leftColumnGroups.includes(group.key)))

function openModal() {
  showModal.value = true
  const available = new Set(Object.keys(props.selectableColumns))
  enabledColumns.value = props.userColumns.filter((col) => available.has(col))
}

watch(showModal, (newShowModal: boolean) => {
  const target: HTMLElement | null = document.getElementById('column-settings')
  if (!newShowModal || !target) {
    return
  }
  const observer: MutationObserver = new MutationObserver(() => {
    const modalSettings: HTMLElement | null = document.getElementById('column-modal-settings')
    if (modalSettings) {
      modalSettings.scrollIntoView({ behavior: 'smooth' })
      observer.disconnect()
    }
  })
  observer.observe(target, {
    attributes: true,
    childList: true
  })
})

function closeModal() {
  showModal.value = false
}

function setColumns(): void {
  emit('set-columns', enabledColumns.value)
  closeModal()
}
</script>

<template>
  <div>
    <div @click="openModal">
      <sancare-octicon
        name="gear"
        :width="15"
        :height="15"
      />
    </div>
    <sancare-modal
      :open="showModal"
      :no-padding="true"
      @close-modal="closeModal"
    >
      <template #header>
        <div id="column-modal-settings">
          <span class="row text-start">
            <span class="sancare-modal-title col">{{ $t('stayList.columnSettings.title') }}</span>
            <span
              class="sancare-modal-close col-auto"
              @click="closeModal"
            >
              <sancare-octicon
                name="x"
                :width="20"
                :height="20"
              />
            </span>
          </span>
        </div>
      </template>
      <div class="row p-3 mt-3">
        <div class="offset-md-1 col-md-5 d-flex flex-column gap-4">
          <SancareCheckboxGroup
            v-for="group in leftGroups"
            :key="group.key"
            v-model="enabledColumns"
            :title="$t(`stayList.columnGroups.${group.key}`)"
            :items="group.items"
          />
        </div>
        <div class="col-md-5 d-flex flex-column gap-4">
          <SancareCheckboxGroup
            v-for="group in rightGroups"
            :key="group.key"
            v-model="enabledColumns"
            :title="$t(`stayList.columnGroups.${group.key}`)"
            :items="group.items"
          />
        </div>
      </div>
      <div class="row mx-3 py-2 justify-content-end">
        <span class="col-auto">
          <button
            class="btn btn-push btn-primary btn-padded"
            @click="setColumns()"
          >
            {{ $t('validate') }}
          </button>
        </span>
      </div>
    </sancare-modal>
  </div>
</template>
